// R6-5 — THE MENU CATALOGS, MOVED VERBATIM OUT OF App.tsx.
//
// Every offering the astrology room, the numerology room, the tarot room and the side menu are drawn
// from. It is data with no JSX in it, and `scripts/gate.mjs` used to reach into App.tsx and slice
// this block out by string offsets to check the A1-TILES roster — a check that would silently pass
// with an EMPTY slice the moment anything above or below it moved. It now imports the real arrays.
//
// The one import here is `import type`, which esbuild erases, so this module still loads with no
// resolution to do — the entry requirement for the gate's loader.
import type { Page } from "../lib/pages";

export type AstrologyTool = {
  id: string;
  title: string;
  text: string;
  action: string;
  room?: Page;
  api?: string;
  format?: "reading" | "pdf";
};

export type AstrologyOffering = {
  id: string;
  title: string;
  items: AstrologyTool[];
};

export type MenuCategory = {
  page: Page;
  title: string;
  summary: string;
  items: string[];
};

export const chartCapabilities: AstrologyTool[] = [
  {
    id: "birth-chart",
    title: "Birth chart",
    text: "Calculate tropical placements, houses, rising sign, and the first layer of interpretation.",
    action: "Get birth chart",
    room: "astrology",
    api: "AstrologyAPI /planets/tropical",
  },
  {
    id: "compatibility",
    title: "Compatibility",
    text: "Open love, zodiac, sun, moon, rising, and relationship chemistry readings.",
    action: "Open compatibility",
    room: "astrology",
    api: "AstrologyAPI compatibility suite",
  },
  {
    id: "transits-moon",
    title: "Transits and moon phases",
    text: "Timing, current weather, lunar rhythm, and spiritual planning prompts.",
    action: "Open timing",
    room: "astrology",
    api: "AstrologyAPI transit and moon tools",
  },
];

export const astrologyOfferings: AstrologyOffering[] = [
  {
    id: "natal-chart",
    title: "Natal chart",
    items: [
      { id: "tropical-birth-chart", title: "Tropical birth chart", text: "Full tropical chart calculation with planets, signs, houses, and chart angles.", action: "Open birth chart", room: "astrology", api: "AstrologyAPI /planets/tropical" },
      { id: "planets-houses", title: "Planets and houses", text: "Planet placements, house positions, sign degrees, direct and retrograde motion.", action: "Open placements", room: "astrology", api: "AstrologyAPI /planets/tropical" },
      { id: "ascendant-angles", title: "Ascendant and angles", text: "Rising sign, chart angles, and the visible entry point of the chart.", action: "Open angles", room: "astrology", api: "AstrologyAPI chart angles" },
      // A1-PURPOSE (2026-08-10): the provenance was `AstrologyAPI karma_destiny_report`, which is a
      // 405 on that plan and was never what delivered this tile. It is AstroAPI's now, and live.
      { id: "career-purpose", title: "Purpose & Destiny", text: "Karma, destiny, calling, and the life-direction signatures in the chart.", action: "Open purpose reading", room: "astrology", api: "AstroAPI /analysis/career" },
    ],
  },
  {
    id: "timing",
    title: "Timing",
    items: [
      { id: "current-transits", title: "Current transits", text: "Current planetary weather against the member's birth chart.", action: "Open transits", room: "astrology", api: "AstrologyAPI transit endpoints" },
      { id: "moon-phases", title: "Moon phases", text: "Lunar phase timing for reflection, planning, ritual, and energetic rhythm.", action: "Open moon timing", room: "astrology", api: "AstrologyAPI moon phase endpoints" },
      { id: "daily-astrology", title: "Daily astrology", text: "Daily timing prompts shaped by today's transits and the member profile.", action: "Open daily reading", room: "daily", api: "AstrologyAPI horoscope endpoints" },
      { id: "horoscopes", title: "Weekly and monthly horoscopes", text: "Weekly and monthly sign-based guidance for planning and reflection.", action: "Open horoscopes", room: "daily", api: "AstrologyAPI horoscope endpoints" },
    ],
  },
  {
    id: "compatibility-suite",
    title: "Compatibility",
    items: [
      { id: "zodiac-compatibility", title: "Zodiac compatibility", text: "Zodiac-level relationship matching when exact birth time is unknown.", action: "Open zodiac match", room: "astrology", api: "AstrologyAPI compatibility endpoints" },
      { id: "love-compatibility", title: "Love compatibility", text: "Romantic chemistry, attraction patterns, and relationship pressure points.", action: "Open love match", room: "astrology", api: "AstrologyAPI love compatibility endpoints" },
      { id: "sun-sign-chemistry", title: "Sun sign chemistry", text: "Core identity compatibility through the solar pattern.", action: "Open sun chemistry", room: "astrology", api: "AstrologyAPI sun compatibility" },
      { id: "moon-rising-connection", title: "Moon and rising connection", text: "Emotional and first-impression compatibility for exact-time charts.", action: "Open moon/rising match", room: "astrology", api: "AstrologyAPI synastry endpoints" },
    ],
  },
  {
    id: "deeper-systems",
    title: "Deeper systems",
    items: [
      { id: "indian-astrology", title: "Indian astrology", text: "Vedic and Indian astrology pathways for deeper traditional chart work.", action: "Open Indian astrology", room: "astrology", api: "AstrologyAPI Indian astrology endpoints" },
      { id: "vedic-chart-options", title: "Vedic chart options", text: "Vedic chart choices, signs, houses, and interpretive layers.", action: "Open Vedic chart", room: "astrology", api: "AstrologyAPI Vedic endpoints" },
      { id: "nakshatra-insights", title: "Nakshatra-style insights", text: "Lunar mansion style insight for character, timing, and relationship pattern.", action: "Open nakshatra insight", room: "astrology", api: "AstrologyAPI Indian astrology endpoints" },
      { id: "relationship-matching", title: "Relationship matching", text: "Deeper matching systems for love, marriage, and long-term relational rhythm.", action: "Open matching", room: "astrology", api: "AstrologyAPI matching endpoints" },
    ],
  },
  {
    id: "reports",
    title: "Reports",
    items: [
      { id: "full-chart-reports", title: "Full chart reports", text: "Long-form astrology reading assembled for the member from chart data and guidance prompts.", action: "Prepare report", room: "astrology", api: "AstrologyAPI PDF/report endpoints", format: "pdf" },
      { id: "saved-reading-history", title: "Saved reading history", text: "A private member archive for past readings, charts, tarot pulls, and notes.", action: "Open history", room: "astrology", api: "12th House member archive" },
    ],
  },
  {
    id: "ai-guidance",
    title: "AI guidance",
    items: [
      { id: "spiritual-timing-prompts", title: "Spiritual timing prompts", text: "Timing prompts for ritual, planning, relationships, visibility, and rest.", action: "Open timing prompts", room: "daily", api: "AstrologyAPI tropical_transits/daily" },
      { id: "future-lala-layer", title: "Future Lala layer", text: "Lalas Inuti Ahari guidance style layered later over the technical chart output.", action: "Open future layer", room: "ask-lala", api: "Private knowledge assistant" },
    ],
  },
];

// N10-3 (owner 2026-07-27) — "WHAT'S IN THIS ROOM" IS THE LEFTOVERS, NOT A SECOND MENU.
// It had grown into a restatement of the page above it: a Timing group that repeats "What's coming?",
// a Deeper systems group that repeats "Traditions and timing". Her ruling is that a duplicate appears
// ONCE, so those groups are hidden HERE, where the real sections already carry them, rather than deleted
// from the data (the side menu and the search index still read the full list).
//
// What survives is the genuine remainder: NATAL CHART (the placements work that has no section of its
// own) and COMPATIBILITY, which she asked to stand as its own group and now does, alone in its column
// instead of buried between two duplicated ones.
export const ASTROLOGY_OFFERINGS_HIDDEN = new Set([
  "reports",        // item 3 (owner): the subscriber archive is off this page
  "ai-guidance",    // item 3 (owner)
  "timing",         // duplicated by the "What's coming?" section
  "deeper-systems", // duplicated by the "Traditions and timing" section
]);

export const astrologyMenuItems = astrologyOfferings.flatMap((offering) => offering.items);

// Compatibility offering ids route to the full-chart compatibility tool (two
// charts) on the astrology page instead of the single-chart reading modal.

export const numerologyCards = [
  ["Life Path", "Core lesson, direction, and long-term spiritual pattern."],
  ["Expression", "The name vibration and how your gifts want to move."],
  ["Soul Urge", "The private desire beneath choices, love, and longing."],
  ["Personal Year", "The cycle shaping this year's openings, endings, and tests."],
];

export const tarotCards = [
  ["Daily card", "A single card for the energy of the day."],
  ["Three-card spread", "Past, present, and next-step guidance."],
  ["Love reading", "Relational patterns, attraction, and emotional truth."],
  ["Work reading", "Career timing, money pressure, visibility, and choice."],
];

export const numerologyTools: AstrologyTool[] = [
  {
    id: "life-path-number",
    title: "Life Path",
    text: "Decode the central road, lessons, and repeating growth pattern carried through the birth date.",
    action: "Open life path",
  },
  {
    id: "expression-number",
    title: "Expression",
    text: "Read the gifts, voice, and public pattern carried through the full birth name.",
    action: "Open expression",
  },
  {
    id: "soul-urge-number",
    title: "Soul Urge",
    text: "Read the private longing, devotion, and inner emotional engine underneath the name.",
    action: "Open soul urge",
  },
  {
    id: "personal-year-number",
    title: "Personal Year",
    text: "Read the yearly cycle shaping timing, choices, release, momentum, and spiritual focus.",
    action: "Open personal year",
  },
];

export const tarotTools: AstrologyTool[] = [
  {
    id: "daily-card",
    title: "Daily card",
    text: "Pull one card for the energy, lesson, and reflection around today.",
    action: "Pull daily card",
  },
  {
    id: "three-card-reading",
    title: "Three-card spread",
    text: "Open a past, present, and near-future reading around a question or focus.",
    action: "Open three cards",
  },
  {
    id: "love-reading",
    title: "Love reading",
    text: "Read relational energy, attraction, tension, compatibility, and spiritual lesson.",
    action: "Open love reading",
  },
  {
    id: "work-reading",
    title: "Work reading",
    text: "Read career timing, money pressure, visibility, choice, and work direction.",
    action: "Open work reading",
  },
];

export const siteMenuCategories: MenuCategory[] = [
  {
    page: "astrology",
    title: "Astrology",
    summary: "Charts, timing, compatibility, transits, and reports.",
    items: astrologyOfferings.flatMap((group) => group.items.map((item) => item.title)),
  },
  {
    page: "numerology",
    title: "Numerology",
    summary: "Name, number, year, and cycle readings.",
    items: numerologyTools.map((item) => item.title),
  },
  {
    page: "tarot",
    title: "Tarot",
    summary: "Daily cards, spreads, love, and work readings.",
    items: tarotTools.map((item) => item.title),
  },
  {
    page: "dreams",
    title: "Dream Interpretation",
    summary: "Symbols, emotions, ancestors, signs, and messages.",
    items: ["Dream journal", "Symbol reading", "Recurring dreams", "Spiritual messages"],
  },
  {
    page: "palmistry",
    title: "Palmistry",
    summary: "A reading of your palm from a photo of your hand.",
    items: ["Heart, head, life lines", "The mounts", "Hand shape", "Markings and signs"],
  },
  {
    page: "library",
    title: "Library",
    summary: "Protected books, videos, transcripts, and saved shelf.",
    items: ["Book shelves", "Sacred texts", "Video teachings", "Saved reading place"],
  },
  {
    page: "seals",
    title: "Create Your Own Seals",
    summary: "English, Latin, Hebrew, Arabic, names, intentions, and symbols.",
    items: ["Name seal", "Intention seal", "Planetary seal", "Saved seal work"],
  },
  {
    page: "spell-book",
    title: "Magic Room",
    summary: "Ritual study, ingredients, transcripts, notes, and timing.",
    items: ["Class transcripts", "Books", "Ritual frameworks", "Spiritual advice"],
  },
];
